import { calculateSaveRate, calculateScore } from './scoreCalculation';

export interface MonthlyMetric {
  month: string; // z.B. "March 2024"
  streams: number;
  listeners: number;
  saves: number;
  followers: number;
  playlists: number;
  score?: number;
}

export interface ProcessedMetric extends MonthlyMetric {
  saveRate: number;
  score: number;
  streamsChange: number | null;
  listenersChange: number | null;
  savesChange: number | null;
  playlistsChange: number | null;
  scoreChange: number | null;
}

export type MetricType = 'score' | 'streams' | 'listeners' | 'saves' | 'playlists' | 'saveRate';

const getMonthYearValues = (dateStr: string) => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const match = dateStr.match(/^([A-Za-z]+)\s(\d{4})$/);
  if (match) {
    const month = months.indexOf(match[1]);
    const year = parseInt(match[2]);
    return { year, month };
  }
  return { year: 0, month: 0 }; // fallback
};

export function getMetricsForYear(metrics: MonthlyMetric[], year: number): ProcessedMetric[] {
  if (!metrics || metrics.length === 0) return [];

  const filtered = metrics.filter(m => {
    const match = m.month.match(/\d{4}/);
    return match && parseInt(match[0]) === year;
  });

  return processAndCalculateChanges(filtered);
}

function processAndCalculateChanges(metrics: MonthlyMetric[]): ProcessedMetric[] {
    const sorted = [...metrics].sort((a, b) => {
      const aVal = getMonthYearValues(a.month);
      const bVal = getMonthYearValues(b.month);
      return aVal.year !== bVal.year ? aVal.year - bVal.year : aVal.month - bVal.month;
    });
  
    const processed: ProcessedMetric[] = [];
  
    for (let i = 0; i < sorted.length; i++) {
      const current = sorted[i];
      const previous = i > 0 ? sorted[i - 1] : null;
  
      // Ensure we handle undefined or null values
      const currentStreams = current.streams || 0;
      const currentSaves = current.saves || 0;
      
      // Calculate save rate correctly as a percentage with 2 decimal places
      const saveRate = currentStreams === 0 ? 0 : Math.round((currentSaves / currentStreams) * 10000) / 100;
 
      const score = calculateScore(
        currentStreams, 
        current.listeners || 0, 
        currentSaves, 
        current.playlists || 0
      );
      
      // Round to 2 decimal places
      const roundedScore = Math.round(score * 100) / 100;
  
      const round = (v: number | null) => v !== null ? Math.round(v) : null;
  
      const streamsChange = previous && previous.streams > 0 
        ? round(((currentStreams - previous.streams) / previous.streams) * 100) 
        : null;
        
      const listenersChange = previous && previous.listeners > 0 
        ? round(((current.listeners - previous.listeners) / previous.listeners) * 100) 
        : null;
        
      const savesChange = previous && previous.saves > 0 
        ? round(((currentSaves - previous.saves) / previous.saves) * 100) 
        : null;
        
      const playlistsChange = previous && previous.playlists > 0 
        ? round(((current.playlists - previous.playlists) / previous.playlists) * 100) 
        : null;
  
      const previousScore = previous 
        ? calculateScore(
            previous.streams || 0, 
            previous.listeners || 0, 
            previous.saves || 0, 
            previous.playlists || 0
          ) 
        : null;
        
      const scoreChange = previousScore !== null 
        ? +(roundedScore - previousScore).toFixed(2) 
        : null;
  
        console.log(saveRate);
      processed.push({
        ...current,
        streams: Math.round(currentStreams),
        listeners: Math.round(current.listeners || 0),
        saves: Math.round(currentSaves),
        playlists: Math.round(current.playlists || 0),
        score: roundedScore,
        saveRate,
        streamsChange,
        listenersChange,
        savesChange,
        playlistsChange,
        scoreChange,
      });
    }
  
    return processed;
  }

export function getLatest12Months(metrics: MonthlyMetric[]): ProcessedMetric[] {
  if (!metrics || metrics.length === 0) return [];
  const sorted = [...metrics].sort((a, b) => {
    const aVal = getMonthYearValues(a.month);
    const bVal = getMonthYearValues(b.month);
    return aVal.year !== bVal.year ? aVal.year - bVal.year : aVal.month - bVal.month;
  });
  const recent12 = sorted.slice(-12);
  return processAndCalculateChanges(recent12);
}